import { z } from 'zod';

/**
 * Factory USB contract (epic &167).
 *
 * Two different transports live here side by side:
 *  - operator API (`/api/factory-mode/*`, called through `apiFetch`) — the key registry;
 *  - provisioning agent bridge (loopback `http://127.0.0.1:8360`) — device discovery and issuing.
 *
 * Zod schemas double as the runtime guard on every payload we render, so a drifted
 * backend or a foreign bridge fails the parse instead of leaking into the UI.
 */

/** Registry statuses of a Factory USB (epic &167 §3.2): INACTIVE → ACTIVE → REVOKED | RETIRED. */
export const factoryUsbStatusSchema = z.enum(['INACTIVE', 'ACTIVE', 'REVOKED', 'RETIRED']);
export type FactoryUsbStatus = z.infer<typeof factoryUsbStatusSchema>;

/** Statuses accepted by the operator API filter — `GET /api/factory-mode/keys?status=`. */
export const factoryUsbStatusFilterSchema = z.enum(['INACTIVE', 'ACTIVE', 'REVOKED', 'RETIRED']);
export type FactoryUsbStatusFilter = z.infer<typeof factoryUsbStatusFilterSchema>;

/**
 * Session lifecycle statuses of the factory protocol (epic &167, factorySession.ts).
 * The registry only ever exposes an ACTIVE one — the terminal states arrive once a
 * heartbeat observes them — but the enum mirrors the server contract in full.
 */
export const factorySessionStatusSchema = z.enum([
  'ACTIVE',
  'COMPLETED',
  'REVOKED',
  'EXPIRED',
  'ABORTED',
]);
export type FactorySessionStatus = z.infer<typeof factorySessionStatusSchema>;

/**
 * The live factory session of a key (#156) — snake_case, the v1 session contract.
 * The server only fills this from an ACTIVE row that is still within its grace and
 * lifetime windows; a stale row is reported as `null`.
 */
export const factoryActiveSessionSchema = z.object({
  session_id: z.string().min(1),
  status: factorySessionStatusSchema,
  last_seen_at: z.string().nullish(),
  session_expires_at: z.string(),
  robot_serial: z.string().min(1),
});
export type FactoryActiveSession = z.infer<typeof factoryActiveSessionSchema>;

/**
 * One registry record of an issued Factory USB.
 * `GET /api/factory-mode/keys` hands back the entity verbatim, so the keys are camelCase.
 */
export const factoryUsbKeySchema = z.object({
  /** Row id of the registry entity — opaque here, accepted as string or number, never rendered. */
  id: z.union([z.string(), z.number()]),
  keyId: z.string().min(1),
  /** Absent while the key is INACTIVE — it becomes an identity only when assigned to a person. */
  employeeId: z.string().nullish(),
  employeeName: z.string().nullish(),
  usbFingerprint: z.string().nullish(),
  status: factoryUsbStatusSchema,
  issuedAt: z.string(),
  expiresAt: z.string().nullish(),
  /** Set by the assignment, not by the issue — ISO|null, null while the key is INACTIVE. */
  assignedAt: z.string().nullish(),
  /** Next planned rotation — registry data only, not a column of the table. */
  rotationDueAt: z.string(),
  lastUsedAt: z.string().nullish(),
  publicKey: z.string().nullish(),
  /**
   * The key's live factory session, or `null` when it has none (#156). The operator
   * table shows "Завершить сессию" only while this is present.
   */
  activeSession: factoryActiveSessionSchema.nullable(),
});
export type FactoryUsbKey = z.infer<typeof factoryUsbKeySchema>;

/** `GET /api/factory-mode/keys` → array of registry records. */
export const factoryUsbKeysSchema = z.array(factoryUsbKeySchema);

/** `POST /api/factory-mode/revoke` and `POST /api/factory-mode/retire` body (terminal operations). */
export const keyActionRequestSchema = z.object({ key_id: z.string().min(1) });
export type KeyActionRequest = z.infer<typeof keyActionRequestSchema>;

/** `POST /api/factory-mode/session/revoke` body — a session is addressed by id or by its key. */
export const revokeSessionRequestSchema = z
  .object({
    session_id: z.string().min(1).optional(),
    key_id: z.string().min(1).optional(),
  })
  .refine((value) => Boolean(value.session_id) || Boolean(value.key_id), {
    message: 'session_id or key_id is required',
  });
export type RevokeSessionRequest = z.infer<typeof revokeSessionRequestSchema>;

/** `GET /bridge/start` → the one-time bridge token kept for the whole wizard run. */
export const bridgeStartSchema = z.object({ token: z.string().min(1) });
export type BridgeStart = z.infer<typeof bridgeStartSchema>;

/**
 * CONTRACT v0 — browser authorization handshake of the provisioning agent (research &121 §4–5).
 *
 * The administrator's own session mints a one-time code on the License Server; the agent trades
 * that code for its machine token, so no `--server` / `--api-key` is ever typed on the workstation.
 * The server side (#142) lands separately, so the exact field names here are agreed rather than
 * implemented: they may still be renamed server-side — a rename fails the parse right here
 * instead of leaking into the screen.
 */
export const bridgeSessionResponseSchema = z.object({
  session_id: z.string().min(1),
  /** One-time, short-lived: the operator shows it to the agent, not to a person. */
  one_time_code: z.string().min(1),
  /** Lifetime of the code in seconds — the wizard counts it down in place. */
  expires_in_s: z.number().int().positive(),
});
export type BridgeSession = z.infer<typeof bridgeSessionResponseSchema>;

/**
 * CONTRACT v0 — `POST /bridge/auth` body: the code handed to an already running agent.
 * Never operator input: the code always comes from the server through the schema above.
 */
export const bridgeAuthRequestSchema = z.object({ code: z.string().trim().min(1) });
export type BridgeAuthRequest = z.infer<typeof bridgeAuthRequestSchema>;

/** Why a mounted medium cannot be issued from (fail-closed reasons of the bridge). */
export const bridgeDeviceReasonSchema = z.enum([
  'notMounted',
  'noKeyFile',
  'noIdentifier',
  'ambiguous',
]);
export type BridgeDeviceReason = z.infer<typeof bridgeDeviceReasonSchema>;

/** A medium as the provisioning agent sees it. Only `selectable` devices may be issued. */
export const bridgeDeviceSchema = z.object({
  id: z.string().min(1),
  label: z.string(),
  mount_point: z.string().nullish(),
  serial: z.string().nullish(),
  wwid: z.string().nullish(),
  selectable: z.boolean(),
  reason: bridgeDeviceReasonSchema.nullish(),
});
export type BridgeDevice = z.infer<typeof bridgeDeviceSchema>;

/** `GET /devices` → candidate media. */
export const bridgeDevicesSchema = z.object({ devices: z.array(bridgeDeviceSchema) });
export type BridgeDevices = z.infer<typeof bridgeDevicesSchema>;

/**
 * Fail-closed candidate list: only media the bridge marked selectable.
 * The wizard issues nothing unless exactly one such candidate is connected.
 */
export function selectableBridgeDevices(devices: BridgeDevice[]): BridgeDevice[] {
  return devices.filter((device) => device.selectable);
}

/** Two-eyes form limits — mirrored by the bridge, enforced here first. */
export const PROVISION_FIELD_MAX = {
  admin: 128,
  supervisor: 128,
} as const;

/**
 * Validation messages are i18n keys, resolved on render — no raw English reaches the UI.
 */
const REQUIRED = { message: 'factoryUsb.wizardValidationRequired' };
const TOO_LONG = { message: 'factoryUsb.wizardValidationTooLong' };

/**
 * Issuing carries no employee any more (v2): the medium is issued INACTIVE and becomes a
 * person's credential later, when an operator assigns it. All that is signed here is the
 * issue itself, by both hands (two-eyes principle).
 */
export const provisionFormSchema = z
  .object({
    admin: z.string().trim().min(1, REQUIRED).max(PROVISION_FIELD_MAX.admin, TOO_LONG),
    supervisor: z.string().trim().min(1, REQUIRED).max(PROVISION_FIELD_MAX.supervisor, TOO_LONG),
  })
  .refine((v) => v.admin !== v.supervisor, {
    path: ['supervisor'],
    message: 'Two distinct signatures are required (admin and supervisor)',
  });
export type ProvisionFormValues = z.infer<typeof provisionFormSchema>;

/** `POST /bridge/provision` body — selected medium plus both signatures. */
// The request keeps the plain-object base: .refine turns provisionFormSchema into
// a ZodEffects (no .extend on it), and the distinct-signatures rule is enforced by the
// bridge and the server anyway — the form-side refine guards the dialog input alone.
const provisionFormBase = z.object({
  admin: z.string().trim().min(1, REQUIRED).max(PROVISION_FIELD_MAX.admin, TOO_LONG),
  supervisor: z.string().trim().min(1, REQUIRED).max(PROVISION_FIELD_MAX.supervisor, TOO_LONG),
});
export const provisionRequestSchema = provisionFormBase.extend({ device: z.string().min(1) });
export type ProvisionRequest = z.infer<typeof provisionRequestSchema>;

/** Assignment form limits — mirrored by the License Server, enforced here first. */
export const ASSIGN_FIELD_MAX = {
  employee_id: 64,
  employee_name: 128,
  requested_by: 128,
} as const;

/**
 * `POST /api/factory-mode/assign` body — snake_case, and the server rejects unknown fields,
 * so this object is built field by field and never spread from a form wholesale.
 */
export const assignRequestSchema = z.object({
  key_id: z.string().trim().min(1, REQUIRED),
  employee_id: z.string().trim().min(1, REQUIRED).max(ASSIGN_FIELD_MAX.employee_id, TOO_LONG),
  employee_name: z.string().trim().min(1, REQUIRED).max(ASSIGN_FIELD_MAX.employee_name, TOO_LONG),
  requested_by: z.string().trim().min(1, REQUIRED).max(ASSIGN_FIELD_MAX.requested_by, TOO_LONG),
});
export type AssignRequest = z.infer<typeof assignRequestSchema>;

/** What the assignment dialog collects: the key is taken from the row, the person from the operator. */
export const assignFormSchema = assignRequestSchema.omit({ key_id: true });
export type AssignFormValues = z.infer<typeof assignFormSchema>;

/** `POST /api/factory-mode/assign` → the freshly activated key, camelCase. */
export const assignResponseSchema = z.object({
  confirmed: z.boolean(),
  keyId: z.string().min(1),
  status: factoryUsbStatusSchema,
  employeeId: z.string().nullish(),
  employeeName: z.string().nullish(),
  assignedAt: z.string().nullish(),
});
export type AssignResponse = z.infer<typeof assignResponseSchema>;

/** Assignment failures the dialog tells apart; everything else gets the generic message. */
export const assignErrorCodeSchema = z.enum([
  'KEY_NOT_INACTIVE',
  'EMPLOYEE_ALREADY_HAS_ACTIVE_KEY',
  'NOT_FOUND',
  'VALIDATION_ERROR',
]);
export type AssignErrorCode = z.infer<typeof assignErrorCodeSchema>;

/**
 * Identifier-looking token inside a server message: hyphenated (`FUSB-0001`) or a single
 * long alphanumeric run that carries a digit. Cyrillic prose never matches.
 */
const KEY_ID_TOKEN = /[A-Za-z0-9]+(?:[-_][A-Za-z0-9]+)+|(?=[A-Za-z0-9]*[0-9])[A-Za-z0-9]{8,}/g;

/**
 * The blocking key id is the last such token: the server names it at the end of the sentence
 * ("… already has active key FUSB-0001") and has no structured field for it yet.
 */
export function extractKeyId(text: string): string | null {
  const tokens = text.match(KEY_ID_TOKEN);
  return tokens && tokens.length > 0 ? (tokens[tokens.length - 1] as string) : null;
}

/**
 * `apiFetch` rejects with an `ApiError`; only its code and the key id it names are read out of
 * it. Anything unexpected degrades to "no code, no key id" and the dialog stays generic.
 *
 * The blocking key id is taken from the structured field first (`details.active_key_id`, added
 * server-side) and only falls back to the text heuristic while the field is absent.
 */
export function readAssignError(error: unknown): {
  code: AssignErrorCode | null;
  keyId: string | null;
} {
  const row = error && typeof error === 'object' ? (error as Record<string, unknown>) : null;
  const parsed = assignErrorCodeSchema.safeParse(row?.errorCode);
  const text = [row?.detail, row?.message]
    .filter((value): value is string => typeof value === 'string')
    .join(' ');

  const details =
    row?.details && typeof row.details === 'object'
      ? (row.details as Record<string, unknown>)
      : null;
  const structuredKeyId =
    typeof details?.active_key_id === 'string' && details.active_key_id.length > 0
      ? details.active_key_id
      : null;

  return {
    code: parsed.success ? parsed.data : null,
    keyId: structuredKeyId ?? extractKeyId(text),
  };
}

/** Issue pipeline stages reported by the bridge, plus the two terminal ones. */
export const provisionStageSchema = z.enum([
  'detecting',
  'generating',
  'writing',
  'verifying',
  'registering',
  'done',
  'failed',
]);
export type ProvisionStage = z.infer<typeof provisionStageSchema>;

/** Non-terminal stages, in the order the bridge walks them. */
export type ProvisionWriteStage = Exclude<ProvisionStage, 'done' | 'failed'>;
export const PROVISION_STAGES: readonly ProvisionWriteStage[] = [
  'detecting',
  'generating',
  'writing',
  'verifying',
  'registering',
];

/** `GET /bridge/provision/{id}` → progress of a running (or finished) issue. */
export const provisionProgressSchema = z.object({
  id: z.string().min(1),
  stage: provisionStageSchema,
  reason: z.string().nullish(),
  key_id: z.string().nullish(),
});
export type ProvisionProgress = z.infer<typeof provisionProgressSchema>;

/** Index of a write stage in the pipeline, `-1` for `done` / `failed`. */
export function provisionWriteStageIndex(stage: ProvisionStage): number {
  return PROVISION_STAGES.indexOf(stage as ProvisionWriteStage);
}

export function isProvisionWriteStage(stage: ProvisionStage): stage is ProvisionWriteStage {
  return provisionWriteStageIndex(stage) >= 0;
}

/** The bridge stops reporting once the issue either succeeded or failed. */
export function isProvisionTerminal(stage: ProvisionStage): boolean {
  return stage === 'done' || stage === 'failed';
}
